/*
 * XYNE Redesign — Long-form FAQ page
 *
 * Three responsibilities:
 *   1. Live search — type to filter FAQ items by question + answer text
 *   2. Scroll-spy — highlight the active category in the sticky nav
 *   3. Anchor permalinks — clicking the link icon next to a question copies
 *      its direct URL to clipboard for sharing
 */
(function () {
  'use strict';

  function init() {
    var page = document.querySelector('[data-rd-faq-pg]');
    if (!page) return;

    var searchInput = page.querySelector('[data-rd-faq-search]');
    var clearBtns = Array.prototype.slice.call(page.querySelectorAll('[data-rd-faq-search-clear]'));
    var countEl = page.querySelector('[data-rd-faq-search-count]');
    var groupsContainer = page.querySelector('[data-rd-faq-groups]');
    var groups = Array.prototype.slice.call(page.querySelectorAll('[data-rd-faq-group]'));
    var items = Array.prototype.slice.call(page.querySelectorAll('[data-rd-faq-item]'));
    var catLinks = Array.prototype.slice.call(page.querySelectorAll('[data-rd-faq-cat-link]'));
    var noResults = page.querySelector('[data-rd-faq-no-results]');
    var noResultsQuery = page.querySelector('[data-rd-faq-no-results-query]');

    // ---------- 1. Live search ----------
    var searchTimer = null;
    function applySearch(rawQuery) {
      var q = (rawQuery || '').trim().toLowerCase();
      var visibleCount = 0;
      var visibleByGroup = {};

      items.forEach(function (item) {
        var text = item.dataset.rdFaqText || '';
        var matches = q === '' || text.indexOf(q) >= 0;
        if (matches) {
          item.hidden = false;
          visibleCount++;
          var groupEl = item.closest('[data-rd-faq-group]');
          if (groupEl) {
            var key = groupEl.dataset.rdFaqGroup;
            visibleByGroup[key] = (visibleByGroup[key] || 0) + 1;
          }
          // Auto-open matched items when there's an active query, so
          // customers see the matched answer without an extra click
          if (q !== '' && q.length >= 2) {
            item.open = true;
          }
        } else {
          item.hidden = true;
          if (q !== '') item.open = false;
        }
      });

      // Hide whole groups that have no visible items
      groups.forEach(function (group) {
        var key = group.dataset.rdFaqGroup;
        group.hidden = !(visibleByGroup[key] && visibleByGroup[key] > 0);
      });

      // No-results state
      if (noResults) {
        if (visibleCount === 0 && q !== '') {
          noResults.hidden = false;
          if (noResultsQuery) noResultsQuery.textContent = q;
          if (groupsContainer) groupsContainer.hidden = true;
        } else {
          noResults.hidden = true;
          if (groupsContainer) groupsContainer.hidden = false;
        }
      }

      // Result count
      if (countEl) {
        if (q === '') {
          countEl.hidden = true;
          countEl.textContent = '';
        } else {
          countEl.hidden = false;
          countEl.textContent = visibleCount + (visibleCount === 1 ? ' match' : ' matches');
        }
      }

      // Show/hide clear button based on input value
      clearBtns.forEach(function (btn) {
        if (btn.matches('[data-rd-faq-search-clear]')) {
          // The clear button beside the input toggles based on query length;
          // the no-results clear button always shows when its parent is visible.
          if (btn.closest('.rd-faq-pg__search')) {
            btn.hidden = q.length === 0;
          }
        }
      });
    }

    if (searchInput) {
      searchInput.addEventListener('input', function () {
        if (searchTimer) clearTimeout(searchTimer);
        searchTimer = setTimeout(function () { applySearch(searchInput.value); }, 80);
      });
      searchInput.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') {
          searchInput.value = '';
          applySearch('');
          searchInput.blur();
        }
      });
    }

    clearBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (searchInput) {
          searchInput.value = '';
          searchInput.focus();
        }
        applySearch('');
      });
    });

    // ---------- 2. Scroll-spy on category nav ----------
    if ('IntersectionObserver' in window && groups.length > 0) {
      var activeKey = null;
      var observer = new IntersectionObserver(function (entries) {
        // Find the topmost-visible group (lowest top value among intersecting entries)
        var visibleGroups = entries
          .filter(function (e) { return e.isIntersecting; })
          .map(function (e) {
            return {
              key: e.target.dataset.rdFaqGroup,
              top: e.boundingClientRect.top
            };
          });

        if (visibleGroups.length === 0) return;

        // Pick the one closest to the top of the viewport (but past the sticky bar offset)
        visibleGroups.sort(function (a, b) { return Math.abs(a.top - 200) - Math.abs(b.top - 200); });
        var newKey = visibleGroups[0].key;

        if (newKey === activeKey) return;
        activeKey = newKey;

        catLinks.forEach(function (link) {
          var match = link.dataset.rdFaqCatLink === activeKey;
          link.setAttribute('aria-current', match ? 'true' : 'false');
        });
      }, {
        rootMargin: '-180px 0px -55% 0px',  // sticky bar offset + bottom buffer
        threshold: [0, 0.1, 0.5]
      });

      groups.forEach(function (group) { observer.observe(group); });
    }

    // ---------- 3. Smooth-scroll category nav clicks ----------
    catLinks.forEach(function (link) {
      link.addEventListener('click', function (e) {
        var key = link.dataset.rdFaqCatLink;
        var target = document.getElementById(key);
        if (!target) return;
        e.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        history.replaceState(null, '', '#' + key);
      });
    });

    // ---------- 4. Anchor copy-to-clipboard for sharing ----------
    page.querySelectorAll('[data-rd-faq-anchor]').forEach(function (anchor) {
      anchor.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();  // don't toggle the <details>
        var item = anchor.closest('[data-rd-faq-item]');
        if (!item || !item.id) return;
        var url = window.location.origin + window.location.pathname + '#' + item.id;
        // Open the question + scroll to it
        item.open = true;
        item.scrollIntoView({ behavior: 'smooth', block: 'start' });
        history.replaceState(null, '', '#' + item.id);
        // Try to copy URL (best-effort)
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(url).then(function () {
            anchor.classList.add('is-copied');
            if (window.rdToast) {
              window.rdToast.success('Link copied to clipboard');
            }
            setTimeout(function () { anchor.classList.remove('is-copied'); }, 1800);
          }).catch(function () {
            /* silent fail — anchor still scrolled */
          });
        }
      });
    });

    // ---------- 5. Auto-open the FAQ item if URL has a hash ----------
    function openHashed() {
      if (!window.location.hash) return;
      var target = document.querySelector(window.location.hash);
      if (target && target.matches('[data-rd-faq-item]')) {
        target.open = true;
      }
    }
    openHashed();
    window.addEventListener('hashchange', openHashed);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
